import React from "react";
import { Row, Col, Button, Form, Card } from "react-bootstrap";
import { Gantt, ViewMode } from "gantt-task-react";
import "gantt-task-react/dist/index.css";
import StageModal from "../StageModal";
import TaskEditModal from "../TaskEditModal";

const ProjectGanttTab = ({
  // Gantt Data
  finalGanttTasks,
  stages,

  // View State
  viewMode,
  columnWidth,

  // UI State
  showClosed,
  setShowClosed,
  sortBy,
  setSortBy,
  showFilterPanel,
  setShowFilterPanel,
  filterStatus,
  setFilterStatus,
  filterPriority,
  setFilterPriority,
  filterAssignee,
  setFilterAssignee,
  searchQuery,
  setSearchQuery,
  timePeriod,
  handleTimePeriodChange,

  // Team Members for Filter
  teamMemberDetails,

  // Handlers
  handleResetFilters,
  scrollToToday,
  handleAutoFit,
  exportPNG,
  exportPDF,
  exportExcel,
  handleTaskChange,

  // Stage Modal
  showStageModal,
  setShowStageModal,
  editingStage,
  setEditingStage,
  projectId,
  loadProjectStages,

  // Task Edit Modal
  showTaskEditModal,
  setShowTaskEditModal,
  editingTaskId,
  setEditingTaskId,
  loadProjectTasksByStages,

  // Collapse State
  collapsedStages,
  setCollapsedStages,
}) => {
  return (
    <div className="project-view-fade-in" style={{ margin: "10px" }}>
      {/* Zoom + Export Controls */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        {/* LEFT SIDE CONTROLS */}
        <div className="d-flex gap-2 align-items-center">
          {/* Closed / Active Toggle */}
          <Button
            size="sm"
            variant={!showClosed ? "primary" : "outline-primary"}
            onClick={() => setShowClosed(false)}
          >
            Active
          </Button>

          <Button
            size="sm"
            variant={showClosed ? "primary" : "outline-primary"}
            onClick={() => setShowClosed(true)}
          >
            Closed
          </Button>

          {/* Sort Dropdown */}
          <Form.Select
            size="sm"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ width: "160px" }}
          >
            <option value="stageStart">Stage Start Date</option>
            <option value="stageEnd">Stage End Date</option>
            <option value="taskStart">Task Start Date</option>
            <option value="taskEnd">Task End Date</option>
            <option value="status">Status</option>
            <option value="priority">Priority</option>
            <option value="assignee">Assignee</option>
            <option value="duration">Duration</option>
          </Form.Select>

          {/* Filter Button */}
          <Button
            size="sm"
            variant="outline-dark"
            onClick={() => setShowFilterPanel(!showFilterPanel)}
          >
            Filter
          </Button>

          <Button
            size="sm"
            variant="outline-danger"
            onClick={handleResetFilters}
          >
            Reset
          </Button>
        </div>

        <div className="d-flex gap-2 align-items-center">
          <Button size="sm" variant="outline-primary" onClick={scrollToToday}>
            Today
          </Button>

          <Form.Select
            size="sm"
            value={timePeriod}
            onChange={(e) => handleTimePeriodChange(e.target.value)}
            style={{ width: "150px" }}
          >
            <option value="Day">Day</option>
            <option value="Week">Week</option>
            <option value="Month">Month</option>
            <option value="Quarter">Quarter</option>
            <option value="Year">Year</option>
            <option value="Flexible">Flexible</option>
          </Form.Select>

          <Button size="sm" variant="outline-success" onClick={handleAutoFit}>
            Auto Fit
          </Button>

          <Button size="sm" variant="outline-dark" onClick={exportPNG}>
            Export PNG
          </Button>

          <Button size="sm" variant="outline-dark" onClick={exportPDF}>
            Export PDF
          </Button>

          <Button size="sm" variant="outline-dark" onClick={exportExcel}>
            Export Excel
          </Button>
        </div>

        <Form.Control
          type="text"
          placeholder="Search stage or task..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: "20vw", borderRadius: "20px" }}
        />
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <Card className="mb-3 p-3 shadow-sm">
          <Row>
            <Col>
              <Form.Label>Status</Form.Label>
              <Form.Select
                multiple
                value={filterStatus}
                onChange={(e) =>
                  setFilterStatus(
                    Array.from(
                      e.target.selectedOptions,
                      (option) => option.value,
                    ),
                  )
                }
              >
                <option>Not Started</option>
                <option>In Progress</option>
                <option>On Hold</option>
                <option>Completed</option>
                <option>Cancelled</option>
              </Form.Select>
            </Col>

            <Col>
              <Form.Label>Priority</Form.Label>
              <Form.Select
                multiple
                value={filterPriority}
                onChange={(e) =>
                  setFilterPriority(
                    Array.from(
                      e.target.selectedOptions,
                      (option) => option.value,
                    ),
                  )
                }
              >
                <option>High</option>
                <option>Medium</option>
                <option>Low</option>
              </Form.Select>
            </Col>

            <Col>
              <Form.Label>Assignee</Form.Label>
              <Form.Select
                multiple
                value={filterAssignee}
                onChange={(e) =>
                  setFilterAssignee(
                    Array.from(
                      e.target.selectedOptions,
                      (option) => option.value,
                    ),
                  )
                }
              >
                {teamMemberDetails.map((user) => (
                  <option key={user.userId} value={String(user.userId)}>
                    {user.username}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card>
      )}

      {/* Gantt Chart */}
      <div id="gantt-container">
        <Card className="shadow-sm border-0">
          <Card.Body>
            {finalGanttTasks.length === 0 ? (
              <div className="text-center p-4">
                No valid tasks for Gantt view
              </div>
            ) : (
              <Gantt
                tasks={finalGanttTasks}
                viewMode={viewMode}
                columnWidth={
                  viewMode === ViewMode.Month
                    ? 300
                    : viewMode === ViewMode.Year
                      ? 350
                      : 65
                }
                listCellWidth="200px"
                onDateChange={handleTaskChange}
                onExpanderClick={(task) => {
                  if (task.type !== "project") return;
                  const stageId = task.id.replace("stage-", "");
                  setCollapsedStages((prev) => ({
                    ...prev,
                    [stageId]: !prev[stageId],
                  }));
                }}
                onDoubleClick={(task) => {
                  if (!task) return;

                  // If Stage → open Stage Modal
                  if (task.type === "project") {
                    const stageId = task.id.replace("stage-", "");
                    const stageData = stages.find(
                      (s) => String(s.stageId) === String(stageId),
                    );
                    if (stageData) {
                      setEditingStage(stageData);
                      setShowStageModal(true);
                    }
                    return;
                  }

                  // If Task → open Task Edit Modal
                  setEditingTaskId(task.id);
                  setShowTaskEditModal(true);
                }}
                todayColor="#ef4444"
              />
            )}

            {/* Stage Modal */}
            <StageModal
              show={showStageModal}
              onHide={() => {
                setShowStageModal(false);
                setEditingStage(null);
              }}
              stage={editingStage}
              projectId={projectId}
              onStageSaved={() => {
                loadProjectStages();
              }}
            />

            {/* Task Edit Modal */}
            <TaskEditModal
              show={showTaskEditModal}
              onHide={() => {
                setShowTaskEditModal(false);
                setEditingTaskId(null);
              }}
              taskId={editingTaskId}
              onTaskUpdated={() => {
                loadProjectTasksByStages();
              }}
            />
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default ProjectGanttTab;
